import { useState } from "react";
import { Loader2, AtSign } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

const initial = { current_password: "", new_email: "" };

const ChangeEmailCard = () => {
  const { user, logout } = useAuth();
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.new_email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (data.new_email.toLowerCase() === (user?.email || "").toLowerCase()) {
      toast.error("That's already your current email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-email", {
        current_password: data.current_password,
        new_email: data.new_email,
      });
      toast.success("Email updated — please sign in with your new email.");
      setData(initial);
      await logout();
      navigate("/admin/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-lg bg-white border border-border p-6"
      data-testid="admin-change-email"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green-100 text-crest-green">
          <AtSign className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-crest-navy">Change login email</h2>
          <p className="text-xs text-muted-foreground">
            Current: <strong className="text-crest-navy">{user?.email}</strong>. You'll be signed out and asked to log in again.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 grid md:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Confirm current password</label>
          <Input
            type="password"
            value={data.current_password}
            onChange={set("current_password")}
            className="mt-2 h-11"
            required
            autoComplete="current-password"
            data-testid="change-email-current-pw"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">New login email</label>
          <Input
            type="email"
            value={data.new_email}
            onChange={set("new_email")}
            placeholder="admin@cresttorconstruction.com"
            className="mt-2 h-11"
            required
            data-testid="change-email-new-email"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="change-email-submit"
          className="md:col-span-3 md:justify-self-start inline-flex items-center justify-center gap-2 rounded-md bg-crest-green px-6 py-3 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200 disabled:opacity-60 h-11"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Update email
        </button>
      </form>
    </section>
  );
};

export default ChangeEmailCard;
